import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react'
import type { Theme } from '@material-ui/core'
import { FormControl, makeStyles } from '@material-ui/core'
import type { Context } from '@paintscout/util/templater'
import type { OverridableValue } from 'paintscout'
import { trimHtmlInnerWhitespace, trimHtmlTrailingWhitespace } from '@paintscout/util/util'
import striptags from 'striptags'
import type { StyleClasses } from '@paintscout/ui/styles'
import { Editor } from '../Editor'
import type { EditorRef, ToolbarConfig } from '../Editor'
import { TipTapResetToolbar } from '../Editor/TipTapResetToolbar'

export interface OverridableTextAreaProps {
  classes?: StyleClasses<typeof useStyles>
  value?: OverridableValue
  required?: boolean
  label?: string
  sublabel?: string
  disabled?: boolean
  toolbar?: ToolbarConfig
  templateContext?: Context
  allowFormatting?: boolean
}

export const useStyles = makeStyles<Theme, OverridableTextAreaProps>({
  root: {},
  editorRoot: ({ allowFormatting }) => ({
    minHeight: allowFormatting ? '10em' : '4em'
  })
})

/**
 * Renders a Input component from material-ui and provides extra props for adding an icon
 */
const OverridableTextArea = forwardRef((props: OverridableTextAreaProps, ref) => {
  const { label, sublabel, required, disabled, toolbar, allowFormatting, templateContext } = props
  const classes = useStyles(props)
  const editorRef: EditorRef = useRef(null)
  const value = useRef(props.value)
  // Seed the editor exactly once. Re-firing setHTML on incidental parent
  // rerenders (e.g. quote patches from the always-on AI chat bar on estimates)
  // can dismiss the iOS Safari keyboard mid-tap.
  const hasInitializedEditor = useRef(false)
  // TipTap's serialization of the seeded default. getHTML() canonicalizes HTML
  // (merges adjacent <ul>/<ol>, normalizes style attributes, etc.), so the raw
  // stored default never matches it byte-for-byte. We compare against this
  // baseline so that purely-structural normalization isn't mistaken for a custom
  // edit. See getUseCustom and issue #20868.
  const seededDefaultHtml = useRef<string>()

  useEffect(() => {
    if (hasInitializedEditor.current) return
    const editorValue = editorRef.current?.getHTML()

    // I'm not exactly sure when/why Editor.getHTML
    // is returning undefined, but this was the case.
    // When that happens, we're just assuming that the
    // editor isn't ready yet.
    if (typeof editorValue === 'undefined') {
      return
    }

    const currentDefault = props.value.default as string
    const currentCustom = props.value?.custom as string

    // Derive useCustom from the *stored* value, not the live editor HTML. At mount
    // the editor's getHTML() can still be empty before TipTap applies its `content`
    // prop; diffing that empty value against a populated default would incorrectly
    // flag the field as custom and persist { use_custom: true, custom: '' } — e.g.
    // on a freshly copied area, whose description would then render blank here and
    // in groups. See issue #20868.
    const seedContent = props.value?.useCustom ? currentCustom : currentDefault
    const { useCustom } = getUseCustom({
      editorValue: seedContent,
      currentDefault,
      currentCustom
    })

    value.current = {
      ...value.current,
      useCustom,
      default: currentDefault
    }

    editorRef.current?.setHTML((useCustom ? currentCustom : currentDefault) ?? '')
    // When we seeded the editor with the default, capture TipTap's own
    // serialization of it as the comparison baseline (see getUseCustom). Without
    // this, getHTML()'s canonicalized HTML never matches the raw default, so the
    // sync effect below flags the field custom — and for a group/copied area
    // whose custom is '' that blanks the editor on the next render. See #20868.
    if (!useCustom) {
      seededDefaultHtml.current = editorRef.current?.getHTML()
    }
    hasInitializedEditor.current = true
  }, [props.value.default])

  // After initial seed, keep value.current in sync with prop-driven default
  // changes so getValue() returns correct data on save, but never touch the
  // editor DOM — that would steal focus from the user's active edit.
  useEffect(() => {
    if (!hasInitializedEditor.current) return
    const editorValue = editorRef.current?.getHTML()
    if (typeof editorValue === 'undefined') return

    const currentDefault = props.value.default as string
    const currentCustom = props.value?.custom as string
    const { useCustom } = getUseCustom({
      editorValue,
      currentDefault: currentDefault,
      currentCustom,
      normalizedDefault: seededDefaultHtml.current
    })

    value.current = {
      ...value.current,
      useCustom,
      default: currentDefault
    }
  }, [props.value.default])

  useImperativeHandle(ref, () => ({
    getValue: () => {
      const editorValue = editorRef.current?.getHTML()
      const currentCustom = value.current?.custom ? String(value.current.custom) : undefined
      const currentDefault = value.current?.default ? String(value.current.default) : undefined

      // If we dont allow formatting, we want to strip the <p> tags from the string so we dont incorrectly think its custom
      const { useCustom, custom } = allowFormatting
        ? getUseCustom({ editorValue, currentCustom, currentDefault, normalizedDefault: seededDefaultHtml.current })
        : getUseCustom({
            editorValue: striptags(editorValue),
            currentCustom: striptags(currentCustom),
            currentDefault: striptags(currentDefault),
            normalizedDefault: seededDefaultHtml.current ? striptags(seededDefaultHtml.current) : undefined
          })
      return {
        ...value.current,
        useCustom,
        custom
      }
    },
    setValue: (value: string) => {
      editorRef.current?.setHTML(value)
    }
  }))

  return (
    <FormControl classes={{ root: classes.root }} fullWidth={true} required={required}>
      <Editor
        classes={{ root: classes.editorRoot, editor: classes.htmlEditor, editorContent: classes.editorContent }}
        label={label}
        sublabel={sublabel}
        content={(value.current?.useCustom ? value.current?.custom : value.current?.default) as string}
        required={required}
        disabled={disabled}
        onReset={disabled ? null : handleResetDescription}
        ref={editorRef}
        toolbar={toolbar}
        templateContext={templateContext}
        Toolbar={allowFormatting ? undefined : TipTapResetToolbar}
      />
    </FormControl>
  )

  function getUseCustom({
    editorValue,
    currentDefault,
    currentCustom,
    normalizedDefault
  }: {
    editorValue: string
    currentDefault: string
    currentCustom: string
    normalizedDefault?: string
  }): { useCustom: boolean; custom?: string } {
    // We were having mismatches between the value being "&"
    // and the editor returning "&amp", htmlDecode takes care of that.
    // Also strip TipTap's default text-align style (text-align: left) which gets injected
    // by the TextAlign extension — since left-align is the default, it carries no semantic
    // meaning and should be ignored when comparing editor content against the default value.
    const stripDefaultAlignment = (html: string) =>
      html ? html.replace(/\s*style="text-align:\s*left;?"\s*/gi, '') : html
    const clean = (html: string) =>
      html ? replaceLtGt(trimHtmlTrailingWhitespace(trimHtmlInnerWhitespace(stripDefaultAlignment(html)))) : undefined
    const cleanEditorValue = clean(editorValue)
    const cleanDefault = clean(currentDefault)
    const cleanCustom = currentCustom ? trimHtmlTrailingWhitespace(trimHtmlInnerWhitespace(currentCustom)) : undefined
    // `normalizedDefault` is TipTap's own serialization of the default, captured
    // when the editor was seeded. getHTML() canonicalizes HTML — merging adjacent
    // <ul>/<ol>, normalizing style attributes (e.g. trailing ";"), etc. — so the
    // raw stored default rarely matches it byte-for-byte. Treat the editor content
    // as "not custom" when it matches EITHER the raw default or that normalized
    // baseline; otherwise a group/copied area (whose custom is '') gets flagged
    // custom and blanks on the next render. See #20868.
    const cleanBaseline = clean(normalizedDefault)
    // Decode entities on both sides for comparison only, so "&" matches "&amp;" without stripping HTML tags
    const editorCompare = normalizeWhitespace(htmlDecode(cleanEditorValue))
    const matchesDefault = editorCompare === normalizeWhitespace(htmlDecode(cleanDefault))
    const matchesBaseline =
      cleanBaseline !== undefined && editorCompare === normalizeWhitespace(htmlDecode(cleanBaseline))
    const useCustom = !matchesDefault && !matchesBaseline
    const custom = useCustom ? cleanEditorValue : cleanCustom
    return {
      useCustom,
      custom
    }
  }

  function normalizeWhitespace(input: string) {
    return input?.replace(/\s+/g, ' ')
  }

  // &lt;X and &gtX being replaced with <X and >X, need to swap back so they dont become tags and hide/break things
  function replaceLtGt(html) {
    return html.replace(/<(\d+)/g, '&lt;$1').replace(/(^|[^a-z"])>(\d+)/gi, '$1&gt;$2')
  }

  function htmlDecode(input: string): string {
    if (!input) {
      return input
    }

    // Roundtrip through DOMParser so entities normalize consistently on both sides
    // of the comparison (e.g. "&" and "&amp;" both serialize to "&amp;"), while
    // preserving tags so formatting-only changes (bullets, bold) are still detected.
    const parser = new DOMParser()
    const doc = parser.parseFromString(input, 'text/html')
    return doc.body.innerHTML ?? ''
  }

  function handleResetDescription() {
    value.current = {
      ...value.current,
      useCustom: false
    }
    editorRef.current?.setHTML(value.current?.default as string)
  }
})

OverridableTextArea.displayName = 'OverridableTextArea'

export default OverridableTextArea
